import PropTypes from "prop-types"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { setPaymentMethod } from "areas/checkout/store/actions"
import Button from "components/ui/button"
import Media from "components/ui/media"
import { submitSecureCheckout } from "helpers/cart"
import useWindowDimensions from "helpers/window-dimensions"
import { cn } from "styles/utils"

const CheckoutButton = ({ cartSummaryRef }) => {
  const [loading, setLoading] = useState(false)
  const [fixedBtn, setFixedBtn] = useState(false)
  const { isMobile } = useWindowDimensions()
  const cancelled = useRef(false)
  const dispatch = useDispatch()
  const summary = useSelector((state) => state.cart.summary)

  useEffect(() => {
    if (!cancelled.current) {
      const requestTick = () => {
        if (cartSummaryRef?.current) {
          const rect = cartSummaryRef.current.getBoundingClientRect()
          if (!rect) return

          const fixed = rect.bottom <= 0 || rect.bottom >= window.innerHeight
          setFixedBtn(fixed)
        }
      }

      const checkoutScrollTick = () => {
        if (isMobile) {
          window.requestAnimationFrame(requestTick)
        } else {
          setFixedBtn(false)
        }
      }

      checkoutScrollTick()
      window.addEventListener("scroll", checkoutScrollTick, false)
      return () => {
        window.removeEventListener("scroll", checkoutScrollTick, false)
      }
    }
  }, [fixedBtn, isMobile, cartSummaryRef?.current])

  useEffect(() => {
    return () => (cancelled.current = true)
  }, [])

  const secureCheckout = (checkoutType = null) => {
    setLoading(true)
    submitSecureCheckout(checkoutType, dispatch, {
      callback: () => {
        if (!cancelled.current) {
          setLoading(false)
        }
        dispatch(setPaymentMethod(null))
      },
    })
  }

  return (
    <div
      className={cn(
        fixedBtn &&
          "tablet:shadow-none tablet:static tablet:w-auto shadow-(--box-shadow) fixed bottom-0 left-0 z-20 w-full bg-white px-16 pb-10 pt-12"
      )}
      style={{ "--box-shadow": "0 -4px 4px 0 rgba(0, 0, 0, 0.15)" }}
    >
      {fixedBtn && (
        <Media
          className="text-14 leading-21 mb-8 flex items-center justify-between font-bold"
          lessThan="tablet"
        >
          <p>Est. Total:</p>
          <div className="flex">
            {summary.order_total !== summary.total && (
              <p className="text-grey-30 mr-6 font-medium">
                <strike data-ge-basket-subtotals>{summary.order_total}</strike>
              </p>
            )}
            <p data-ge-basket-totals>{summary.total}</p>
          </div>
        </Media>
      )}
      <Button
        className="text-18 w-full rounded"
        data-cy={`cart-checkout-btn-${isMobile ? "mobile" : "desktop"}`}
        disabled={loading}
        loading={loading}
        onClick={() => secureCheckout()}
        variant="secondary"
      >
        Checkout
      </Button>
    </div>
  )
}

CheckoutButton.propTypes = {
  cartSummaryRef: PropTypes.object,
}

export default CheckoutButton
